import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import EventApi from "../../services/event";
import WhatsAppService from "../../services/whatsapp";
import { config } from "../../config";

// ── NOTA: Este arquivo foi redesenhado para a identidade visual do ANM 2026.
// Paleta: azul-escuro/roxo/ciano (#09D5E2), assets: fundo.png, anm26.svg,
// aviao-frente.png, nuvens.png, torres.png, aviao-decolando.png

// ── Paleta ANM 2026 ────────────────────────────────────────────────────────────
const CYAN = "#09D5E2";
const PURPLE = "#7C3AED";

// ── Inline Styles ─────────────────────────────────────────────────────────────
const S = {
    page: {
        minHeight: "100vh",
        background: `url('/fundo.png') center center / cover no-repeat fixed`,
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        color: "#f0f4ff",
        position: "relative",
        overflowX: "hidden",
    },
    pageDimmer: {
        position: "fixed",
        inset: 0,
        background: "linear-gradient(160deg, rgba(7,8,30,0.84) 0%, rgba(10,22,80,0.74) 50%, rgba(5,5,20,0.90) 100%)",
        pointerEvents: "none",
        zIndex: 0,
    },

    // ── Header
    header: {
        position: "relative",
        zIndex: 10,
        background: "rgba(5,8,30,0.78)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: `1px solid rgba(9,213,226,0.18)`,
        padding: "14px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        flexWrap: "wrap",
    },
    headerLogo: {
        height: "44px",
        width: "auto",
        display: "block",
    },
    headerBadge: {
        background: `linear-gradient(135deg, ${CYAN}, ${PURPLE})`,
        borderRadius: "50px",
        padding: "5px 18px",
        fontSize: "11px",
        fontWeight: "800",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "#fff",
        whiteSpace: "nowrap",
        boxShadow: `0 0 16px rgba(9,213,226,0.35)`,
    },

    // ── Hero
    heroWrapper: {
        position: "relative",
        zIndex: 5,
        overflow: "hidden",
        paddingTop: "32px",
        textAlign: "center",
    },
    heroNuvens: {
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "clamp(520px, 90vw, 1100px)",
        opacity: 0.4,
        pointerEvents: "none",
        userSelect: "none",
    },
    heroAviao: {
        display: "block",
        margin: "0 auto",
        width: "clamp(200px, 38vw, 480px)",
        filter: "drop-shadow(0 0 40px rgba(9,213,226,0.5))",
        position: "relative",
        zIndex: 2,
        userSelect: "none",
    },
    heroTitle: {
        fontSize: "clamp(24px, 5vw, 50px)",
        fontWeight: "900",
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
        margin: "16px 0 8px",
        background: `linear-gradient(130deg, #fff 25%, ${CYAN} 70%, ${PURPLE} 100%)`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        position: "relative",
        zIndex: 2,
    },
    heroSub: {
        color: "rgba(200,220,255,0.78)",
        fontSize: "clamp(13px, 1.8vw, 16px)",
        margin: "0 auto 18px",
        maxWidth: "480px",
        lineHeight: 1.5,
        position: "relative",
        zIndex: 2,
    },
    metaBar: {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "10px",
        marginBottom: "28px",
        position: "relative",
        zIndex: 2,
    },
    metaChip: {
        background: "rgba(9,213,226,0.10)",
        border: `1px solid rgba(9,213,226,0.30)`,
        borderRadius: "50px",
        padding: "7px 22px",
        fontSize: "13px",
        fontWeight: "700",
        color: CYAN,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        backdropFilter: "blur(8px)",
        letterSpacing: "0.03em",
    },

    // ── Form card
    card: {
        background: "rgba(8,14,50,0.74)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: `1px solid rgba(9,213,226,0.14)`,
        borderRadius: "24px",
        padding: "clamp(20px, 5vw, 44px)",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset",
        position: "relative",
        overflow: "hidden",
    },
    cardGlowTL: {
        position: "absolute",
        top: "-60px",
        left: "-60px",
        width: "220px",
        height: "220px",
        borderRadius: "50%",
        background: `radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)`,
        pointerEvents: "none",
    },
    cardGlowBR: {
        position: "absolute",
        bottom: "-60px",
        right: "-60px",
        width: "220px",
        height: "220px",
        borderRadius: "50%",
        background: `radial-gradient(circle, rgba(9,213,226,0.18) 0%, transparent 70%)`,
        pointerEvents: "none",
    },

    // ── Section title
    sectionTitle: {
        fontSize: "11px",
        fontWeight: "800",
        color: CYAN,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        marginTop: "28px",
        marginBottom: "18px",
        paddingBottom: "10px",
        borderBottom: `1px solid rgba(9,213,226,0.15)`,
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    sectionDot: {
        width: "7px",
        height: "7px",
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${CYAN}, ${PURPLE})`,
        boxShadow: `0 0 8px ${CYAN}`,
        flexShrink: 0,
    },

    // ── Grid / Fields
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        gap: "14px",
        marginBottom: "8px",
    },
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    label: {
        fontSize: "11px",
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: "rgba(180,210,255,0.75)",
    },
    input: {
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(9,213,226,0.18)",
        borderRadius: "10px",
        padding: "11px 14px",
        fontSize: "14px",
        color: "#e8f0ff",
        outline: "none",
        width: "100%",
        boxSizing: "border-box",
        transition: "border-color 0.2s, box-shadow 0.2s",
        fontFamily: "inherit",
    },
    inputError: {
        border: "1px solid rgba(239,68,68,0.7)",
    },
    select: {
        background: "rgba(8,14,50,0.88)",
        border: "1px solid rgba(9,213,226,0.18)",
        borderRadius: "10px",
        padding: "11px 14px",
        fontSize: "14px",
        color: "#e8f0ff",
        outline: "none",
        width: "100%",
        boxSizing: "border-box",
        cursor: "pointer",
        fontFamily: "inherit",
        appearance: "auto",
    },
    errorMsg: {
        fontSize: "11px",
        color: "#f87171",
        marginTop: "2px",
    },
    sponsorNote: {
        fontSize: "12px",
        color: "#fbbf24",
        marginTop: "4px",
    },
    submitBtn: {
        display: "block",
        width: "100%",
        padding: "16px",
        marginTop: "28px",
        background: `linear-gradient(135deg, ${CYAN} 0%, #0ea5e9 40%, ${PURPLE} 100%)`,
        border: "none",
        borderRadius: "12px",
        color: "#fff",
        fontSize: "15px",
        fontWeight: "800",
        letterSpacing: "0.08em",
        cursor: "pointer",
        transition: "opacity 0.2s, transform 0.15s, box-shadow 0.2s",
        textTransform: "uppercase",
        boxShadow: `0 4px 24px rgba(9,213,226,0.35), 0 0 0 1px rgba(255,255,255,0.08) inset`,
        fontFamily: "inherit",
    },
    submitBtnDisabled: {
        opacity: 0.45,
        cursor: "not-allowed",
        boxShadow: "none",
    },
    successBox: {
        textAlign: "center",
        padding: "48px 24px",
    },
    successIconWrap: {
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        background: `linear-gradient(135deg, rgba(9,213,226,0.15), rgba(52,211,153,0.15))`,
        border: `2px solid rgba(52,211,153,0.4)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 20px",
        fontSize: "36px",
    },
    successTitle: {
        fontSize: "26px",
        fontWeight: "900",
        background: "linear-gradient(135deg, #34d399, #6ee7b7)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        marginBottom: "12px",
    },
    successText: {
        fontSize: "15px",
        color: "rgba(180,210,255,0.85)",
        lineHeight: 1.7,
    },
    notFoundBox: {
        textAlign: "center",
        padding: "60px 20px",
        color: "#f87171",
        fontSize: "20px",
        fontWeight: "700",
    },
    // termos
    termsContainer: {
        marginTop: "24px",
        padding: "16px 18px",
        background: "rgba(9,213,226,0.05)",
        border: "1px solid rgba(9,213,226,0.14)",
        borderRadius: "12px",
    },
    termsCheckbox: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
    },
    termsLink: {
        color: CYAN,
        cursor: "pointer",
        textDecoration: "underline",
        fontWeight: "700",
    },
    termsBox: {
        marginTop: "16px",
        padding: "18px",
        borderRadius: "10px",
        background: "rgba(0,0,0,0.25)",
        border: "1px solid rgba(9,213,226,0.12)",
        color: "#cdd9f8",
        lineHeight: 1.75,
        fontSize: "14px",
    },
    termsClose: {
        marginTop: "14px",
        color: CYAN,
        cursor: "pointer",
        fontWeight: "700",
        display: "inline-block",
    },
};

// ── Inject global CSS once (keyframes + Google Font + focus styles) ────────────
if (typeof document !== "undefined" && !document.getElementById("anm26-styles")) {
    const style = document.createElement("style");
    style.id = "anm26-styles";
    style.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
        #anm26-root { font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; }
        #anm26-root * { font-family: inherit; }

        @keyframes floatPlane  { 0%{transform:translateY(0) rotate(-1deg)} 50%{transform:translateY(-14px) rotate(1deg)} 100%{transform:translateY(0) rotate(-1deg)} }
        @keyframes floatSlowly { 0%{transform:translateY(0)}  50%{transform:translateY(-8px)}  100%{transform:translateY(0)} }
        @keyframes shimmerOpac { 0%{opacity:0.7} 50%{opacity:1}  100%{opacity:0.7} }

        .anm26-plane { animation: floatPlane 5s ease-in-out infinite; }
        .anm26-towers{ animation: floatSlowly 6s ease-in-out infinite; }
        .anm26-decolando { animation: shimmerOpac 4s ease-in-out infinite; }

        #anm26-root input:focus,
        #anm26-root select:focus {
            outline: none !important;
            border-color: #09D5E2 !important;
            box-shadow: 0 0 0 3px rgba(9,213,226,0.15) !important;
        }
        #anm26-root input::placeholder { color: rgba(180,210,255,0.32); }
        #anm26-root select option { background: #090e2e; color: #e8f0ff; }
        #anm26-root input[type="checkbox"] { accent-color: #09D5E2; }

        #btn-submit:hover:not(:disabled) {
            opacity: 0.88;
            transform: translateY(-2px);
            box-shadow: 0 10px 36px rgba(9,213,226,0.55) !important;
        }
        #btn-submit:active:not(:disabled) { transform: translateY(0); }

        /* Side deco: hidden on mobile, shown on desktop */
        .anm26-side-deco { display: none; }
        .anm26-layout {
            max-width: 1120px;
            margin: 0 auto;
            padding: 0 16px 80px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 0;
        }
        @media (min-width: 1024px) {
            .anm26-layout {
                grid-template-columns: 260px 1fr;
                gap: 40px;
                align-items: start;
            }
            .anm26-side-deco {
                display: flex !important;
                flex-direction: column;
                align-items: center;
                gap: 24px;
                position: sticky;
                top: 24px;
                padding-top: 8px;
            }
        }
    `;
    document.head.appendChild(style);
}

// ── CPF Validator ─────────────────────────────────────────────────────────────
function isValidCPF(cpf) {
    const cleaned = cpf.replace(/\D/g, "");
    if (cleaned.length !== 11 || /^(\d)\1+$/.test(cleaned)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cleaned[i]) * (10 - i);
    let r = (sum * 10) % 11;
    if (r === 10 || r === 11) r = 0;
    if (r !== parseInt(cleaned[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cleaned[i]) * (11 - i);
    r = (sum * 10) % 11;
    if (r === 10 || r === 11) r = 0;
    return r === parseInt(cleaned[10]);
}

// ── Email Validator ───────────────────────────────────────────────────────────
function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ── Brazilian States ──────────────────────────────────────────────────────────
const BRAZILIAN_STATES = [
    { value: "AC", label: "Acre" },
    { value: "AL", label: "Alagoas" },
    { value: "AP", label: "Amapá" },
    { value: "AM", label: "Amazonas" },
    { value: "BA", label: "Bahia" },
    { value: "CE", label: "Ceará" },
    { value: "DF", label: "Distrito Federal" },
    { value: "ES", label: "Espírito Santo" },
    { value: "GO", label: "Goiás" },
    { value: "MA", label: "Maranhão" },
    { value: "MT", label: "Mato Grosso" },
    { value: "MS", label: "Mato Grosso do Sul" },
    { value: "MG", label: "Minas Gerais" },
    { value: "PA", label: "Pará" },
    { value: "PB", label: "Paraíba" },
    { value: "PR", label: "Paraná" },
    { value: "PE", label: "Pernambuco" },
    { value: "PI", label: "Piauí" },
    { value: "RJ", label: "Rio de Janeiro" },
    { value: "RN", label: "Rio Grande do Norte" },
    { value: "RS", label: "Rio Grande do Sul" },
    { value: "RO", label: "Rondônia" },
    { value: "RR", label: "Roraima" },
    { value: "SC", label: "Santa Catarina" },
    { value: "SP", label: "São Paulo" },
    { value: "SE", label: "Sergipe" },
    { value: "TO", label: "Tocantins" }
];

// ── Area Options by Sector ────────────────────────────────────────────────────
const AREA_OPTIONS = {
    Público: [
        "Setor Aeroportuário",
        "Outros setores da economia"
    ],
    Privado: [
        "Setor Aeroportuário",
        "Outros setores da economia",
        "Escritório de Advocacia",
        "Consultor(a)",
        "Entidades Associativas e Sindicais",
        "Instituição de Ensino",
        "Staff",
        "Outros"
    ]
};

// ── Field Component ────────────────────────────────────────────────────────────
const Field = React.memo(({ id, label, name, type = "text", mask, placeholder, required, value, onChange, onBlur, error, disabled, maxLength }) => (
    <div style={S.fieldGroup}>
        <label htmlFor={id} style={S.label}>{label}{required ? " *" : ""}</label>
        {mask ? (
            <InputMask
                mask={mask}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maskChar=""
                disabled={disabled}
            >
                {() => (
                    <input
                        id={id}
                        name={name}
                        type={type}
                        placeholder={placeholder}
                        style={{ ...S.input, ...(error ? S.inputError : {}) }}
                        disabled={disabled}
                        maxLength={maxLength}
                    />
                )}
            </InputMask>
        ) : (
            <input
                id={id}
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                style={{ ...S.input, ...(error ? S.inputError : {}) }}
                disabled={disabled}
                autoComplete="off"
                maxLength={maxLength}
            />
        )}
        {error && <span style={S.errorMsg}>⚠ {error}</span>}
    </div>
));

// ── Select Field Component ─────────────────────────────────────────────────────
const SelectField = React.memo(({ id, label, name, options, required, value, onChange, error, disabled, placeholder }) => (
    <div style={S.fieldGroup}>
        <label htmlFor={id} style={S.label}>{label}{required ? " *" : ""}</label>
        <select
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            style={{ ...S.select, ...(error ? S.inputError : {}) }}
            disabled={disabled}
        >
            <option value="">{placeholder || "Selecione uma opção"}</option>
            {options.map((opt) => (
                <option key={opt.value || opt} value={opt.value || opt}>
                    {opt.label || opt}
                </option>
            ))}
        </select>
        {error && <span style={S.errorMsg}>⚠ {error}</span>}
    </div>
));

// ── Checkbox Field Component ───────────────────────────────────────────────────
const CheckboxField = React.memo(({ id, label, name, checked, onChange, disabled }) => (
    <div style={{ ...S.fieldGroup, flexDirection: "row", alignItems: "center", gap: "10px" }}>
        <input
            id={id}
            name={name}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            style={{ width: "18px", height: "18px", cursor: "pointer", flexShrink: 0 }}
        />
        <label
            htmlFor={id}
            style={{
                ...S.label,
                margin: 0,
                cursor: "pointer",
                textTransform: "none",
                letterSpacing: "0",
                fontSize: "13px",
                color: "rgba(200,220,255,0.85)",
            }}
        >
            {label}
        </label>
    </div>
));

// ── Section Title helper ───────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
    <p style={S.sectionTitle}>
        <span style={S.sectionDot} />
        {children}
    </p>
);

// ── Visual divider ────────────────────────────────────────────────────────────
const Divider = () => (
    <div style={{
        height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(9,213,226,0.14), transparent)",
        margin: "8px 0 24px",
    }} />
);

// ── Main Component ────────────────────────────────────────────────────────────
const DEFAULT_EVENT_ID = 1;

const SubscribeToEvent = (props) => {
    // Props para controlar se mostra campo de patrocinador
    const showSponsorField = props.showSponsorField !== false; // Por padrão mostra
    // Patrocinador travado (vindo de SponsoredRegister)
    const lockedSponsor = props.lockedSponsor || null;

    const [event, setEvent] = useState(null);
    const [sponsors, setSponsors] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        cpf: "",
        email: "",
        emailConfirmation: "",
        phone: "",
        company: "",
        role: "",
        sponsor_id: "",
        // Novos campos
        name_tag: "",
        event_category: "",
        passport: "",
        gender: "",
        extras: "",
        custom_fields: "",
        address: "",
    });
    const [isForeigner, setIsForeigner] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [eventNotFound, setEventNotFound] = useState(false);

    // termo
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    // Preenche sponsor_id automaticamente quando lockedSponsor é fornecido
    useEffect(() => {
        if (lockedSponsor?.id) {
            setFormData((prev) => ({ ...prev, sponsor_id: String(lockedSponsor.id) }));
        }
    }, [lockedSponsor]);

    // Fetch event + sponsors on mount
    useEffect(() => {
        const eventId = props.match?.params?.event_id || DEFAULT_EVENT_ID;
        async function load() {
            console.log('[SubscribeToEvent] Fetching event:', eventId);
            const respEvent = await EventApi.getEvent(eventId);
            console.log('[SubscribeToEvent] Response:', respEvent);
            if (respEvent?.data?.data) {
                setEvent(respEvent.data.data);
                // Só carrega patrocinadores se o campo deve ser exibido
                if (showSponsorField) {
                    const respSponsors = await EventApi.listSponsors({ event_id: eventId });
                    if (respSponsors?.data?.data) {
                        setSponsors(respSponsors.data.data);
                    }
                }
            } else {
                console.error('[SubscribeToEvent] Event not found. Full response:', respEvent);
                setEventNotFound(true);
            }
        }
        load();
    }, [showSponsorField]);

    const selectedSponsor = sponsors.find(
        (s) => String(s.id) === String(formData.sponsor_id)
    );
    // Para sponsor travado, usa diretamente o lockedSponsor
    const activeSponsor = lockedSponsor || selectedSponsor;
    const sponsorFull =
        activeSponsor &&
        activeSponsor.total_slots > 0 &&
        activeSponsor.occupied_slots >= activeSponsor.total_slots;

    // ── Change handler ──────────────────────────────────────────────────────
    function onChange(e) {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Validação em tempo real para confirmação de e-mail
        if (name === "emailConfirmation" && formData.email) {
            if (value.trim().toLowerCase() !== formData.email.trim().toLowerCase()) {
                setErrors((prev) => ({ ...prev, emailConfirmation: "Os e-mails não coincidem" }));
            } else {
                setErrors((prev) => ({ ...prev, emailConfirmation: null }));
            }
        } else if (name === "email" && formData.emailConfirmation) {
            // Re-valida confirmação quando o email principal muda
            if (value.trim().toLowerCase() !== formData.emailConfirmation.trim().toLowerCase()) {
                setErrors((prev) => ({ ...prev, emailConfirmation: "Os e-mails não coincidem" }));
            } else {
                setErrors((prev) => ({ ...prev, emailConfirmation: null }));
            }
        } else if (errors[name]) {
            // Limpa erros de validação quando usuário está digitando
            setErrors((prev) => ({ ...prev, [name]: null }));
        }

        // Lógica condicional Setor/Área
        if (name === "extras") {
            // Limpa custom_fields se o valor atual não existe nas novas opções
            const newAreaOptions = AREA_OPTIONS[value] || [];
            if (formData.custom_fields && !newAreaOptions.includes(formData.custom_fields)) {
                setFormData((prev) => ({ ...prev, custom_fields: "" }));
            }
        }
    }

    // ── Blur handler para validação de email, name e phone ──────────────────
    function onBlur(e) {
        const { name, value } = e.target;

        // Validação de nome (mínimo 3 caracteres)
        if (name === "name") {
            if (value && value.trim().length < 3) {
                setErrors((prev) => ({ ...prev, name: "Nome completo inválido (mínimo 3 caracteres)" }));
            } else if (value && value.trim().length >= 3) {
                setErrors((prev) => ({ ...prev, name: null }));
            }
        }

        // Validação de telefone (formato completo com +55)
        if (name === "phone") {
            const phoneDigits = value.replace(/\D/g, "");
            if (value && phoneDigits.length > 0 && phoneDigits.length !== 13) {
                setErrors((prev) => ({ ...prev, phone: "Telefone deve ter formato completo (+55 (99) 9 9999-9999)" }));
            } else if (value && phoneDigits.length === 13) {
                setErrors((prev) => ({ ...prev, phone: null }));
            }
        }

        // Validação de formato de email no onBlur
        if (name === "email") {
            if (value && !isValidEmail(value)) {
                setErrors((prev) => ({ ...prev, email: "E-mail inválido" }));
            } else if (value && isValidEmail(value)) {
                setErrors((prev) => ({ ...prev, email: null }));
            }

            // Re-valida a confirmação se já foi preenchida
            if (formData.emailConfirmation && value) {
                if (value.trim().toLowerCase() !== formData.emailConfirmation.trim().toLowerCase()) {
                    setErrors((prev) => ({ ...prev, emailConfirmation: "Os e-mails não coincidem" }));
                } else {
                    setErrors((prev) => ({ ...prev, emailConfirmation: null }));
                }
            }
        } else if (name === "emailConfirmation") {
            // Validação de formato e matching
            if (value && !isValidEmail(value)) {
                setErrors((prev) => ({ ...prev, emailConfirmation: "E-mail inválido" }));
            } else if (value && formData.email) {
                if (value.trim().toLowerCase() !== formData.email.trim().toLowerCase()) {
                    setErrors((prev) => ({ ...prev, emailConfirmation: "Os e-mails não coincidem" }));
                } else {
                    setErrors((prev) => ({ ...prev, emailConfirmation: null }));
                }
            }
        }
    }

    // ── Handler para checkbox isForeigner ──────────────────────────────────
    function onForeignerChange(e) {
        const checked = e.target.checked;
        setIsForeigner(checked);

        // Limpa campos condicionais
        if (checked) {
            // Marcou como estrangeiro: limpa CPF e Estado
            setFormData((prev) => ({ ...prev, cpf: "", address: "" }));
            setErrors((prev) => ({ ...prev, cpf: null, address: null }));
        } else {
            // Desmarcou: limpa Passaporte
            setFormData((prev) => ({ ...prev, passport: "" }));
            setErrors((prev) => ({ ...prev, passport: null }));
        }
    }

    // ── Validate ────────────────────────────────────────────────────────────
    function validate() {
        const newErrors = {};

        // Nome completo
        if (!formData.name.trim() || formData.name.trim().length < 3)
            newErrors.name = "Nome completo inválido (mínimo 3 caracteres)";

        // Nome do crachá
        if (!formData.name_tag.trim())
            newErrors.name_tag = "Nome do crachá é obrigatório";

        // Categoria
        if (!formData.event_category)
            newErrors.event_category = "Selecione uma categoria";

        // CPF ou Passaporte (condicional)
        if (!isForeigner) {
            if (!isValidCPF(formData.cpf))
                newErrors.cpf = "CPF inválido";
            // Estado é obrigatório para brasileiros
            if (!formData.address)
                newErrors.address = "Selecione um estado";
        } else {
            // Passaporte deve ter pelo menos 5 caracteres
            if (!formData.passport || formData.passport.trim().length < 5)
                newErrors.passport = "Passaporte inválido (mínimo 5, máximo 35 caracteres)";
            if (formData.passport && formData.passport.trim().length > 35)
                newErrors.passport = "Passaporte muito longo (máximo 35 caracteres)";
        }

        // Email
        if (!isValidEmail(formData.email))
            newErrors.email = "E-mail inválido";
        if (!isValidEmail(formData.emailConfirmation))
            newErrors.emailConfirmation = "Confirmação de e-mail inválida";
        if (formData.email.trim().toLowerCase() !== formData.emailConfirmation.trim().toLowerCase())
            newErrors.emailConfirmation = "Os e-mails não coincidem";

        // Telefone - formato completo com código do país (+55)
        const phoneDigits = formData.phone.replace(/\D/g, "");
        if (phoneDigits.length === 0) {
            newErrors.phone = "Telefone é obrigatório";
        } else if (phoneDigits.length !== 13) {
            newErrors.phone = "Telefone deve ter formato completo (+55 (99) 9 9999-9999)";
        }

        // Gênero
        if (!formData.gender)
            newErrors.gender = "Selecione um gênero";

        // Dados profissionais
        if (!formData.company.trim())
            newErrors.company = "Empresa / instituição é obrigatória";
        if (!formData.role.trim())
            newErrors.role = "Cargo é obrigatório";
        if (!formData.extras)
            newErrors.extras = "Selecione um setor";
        if (!formData.custom_fields)
            newErrors.custom_fields = "Selecione uma área";

        // Só valida patrocinador se o campo está visível
        // Patrocinador selecionado (dropdown visível)
        if (showSponsorField && !formData.sponsor_id)
            newErrors.sponsor_id = "Selecione um patrocinador";
        // lockedSponsor não requer validação

        // Termos
        if (!acceptedTerms)
            newErrors.acceptedTerms = "É necessário aceitar os Termos de Uso e a Política de Privacidade.";

        return newErrors;
    }

    // ── Submit ──────────────────────────────────────────────────────────────
    async function handleSubmit(e) {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.warn("Preencha todos os campos corretamente");
            return;
        }
        if (sponsorFull) {
            toast.error(`Limite de vagas atingido para o patrocinador "${selectedSponsor.name}"`);
            return;
        }

        setLoading(true);
        try {
            const now = new Date().toISOString();
            // Aprovação automática quando a inscrição vem de uma página de patrocinador
            const isAutoApproved = Boolean(lockedSponsor?.id);

            const payload = {
                name: formData.name.trim(),
                name_tag: formData.name_tag.trim(),
                event_category: formData.event_category,
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.replace(/\D/g, ""),
                gender: formData.gender,
                company: formData.company.trim(),
                role: formData.role.trim(),
                extras: formData.extras,
                custom_fields: formData.custom_fields,
                event_id: event?.id ? String(event.id) : String(DEFAULT_EVENT_ID),
                enable: true,
                vip: false,
                certificate: 0,
                status_id: 1,
                solicitation_approved: isAutoApproved,
                approved_at: isAutoApproved ? now : null,
                is_guest: false,
            };

            // CPF ou Passaporte - condicional
            if (isForeigner) {
                // Estrangeiro: cpf recebe PASS- + passaporte
                payload.cpf = `PASS-${formData.passport.trim()}`;
                // address não é enviado ou é vazio
            } else {
                // Brasileiro: cpf normal e address
                payload.cpf = formData.cpf.replace(/\D/g, "");
                payload.address = formData.address;
            }

            // Patrocinador: locked tem prioridade sobre selecionado no dropdown
            if (lockedSponsor?.id) {
                payload.sponsor_id = parseInt(lockedSponsor.id);
            } else if (showSponsorField && formData.sponsor_id) {
                payload.sponsor_id = parseInt(formData.sponsor_id);
            }

            const resp = await EventApi.createPerson(payload);

            if (resp?.data && (resp.data.id || resp.data.name)) {
                // Se aprovação automática, envia e-mail e WhatsApp de confirmação
                if (isAutoApproved) {
                    const personId = resp.data.id;
                    const qrcode = `${config.QRCODE_HOST}/?size=200x200&data=${personId}`;
                    EventApi.sendConfirmationMail(payload.name, payload.email, qrcode)
                        .catch((err) => console.error("[SponsoredRegister] Erro ao enviar e-mail:", err));
                    WhatsAppService.sendConfirmacaoInscricao({
                        phone: payload.phone,
                        personId,
                    }).catch((err) => console.warn("[SponsoredRegister] Falha WhatsApp:", err?.message ?? err));
                }
                setSubmitted(true);
            } else if (resp?.response?.data?.message) {
                const msg = resp.response.data.message;
                if (msg.includes("Limite de vagas atingido")) {
                    toast.error(msg);
                } else if (msg.includes("Already used email")) {
                    toast.error("Este e-mail já está cadastrado");
                } else if (msg.includes("Already used cpf")) {
                    toast.error("Este CPF já está cadastrado");
                } else {
                    toast.error(msg);
                }
            } else {
                toast.error("Não foi possível concluir a inscrição. Tente novamente.");
            }
        } catch (err) {
            toast.error("Erro inesperado. Entre em contato com a organização.");
        } finally {
            setLoading(false);
        }
    }

    // ── Page layout ─────────────────────────────────────────────────────────
    return (
        <div id="anm26-root" style={S.page}>
            {/* Background dimmer */}
            <div style={S.pageDimmer} />

            {/* Header */}
            <header style={S.header}>
                <img src="/anm26.svg" alt="Airport National Meeting 2026" style={S.headerLogo} />
                <span style={S.headerBadge}>Inscrições Abertas · 2026</span>
            </header>

            {/* Hero */}
            <div style={S.heroWrapper}>
                <img
                    src="/aviao-frente.png"
                    alt="Avião ANM 2026"
                    style={S.heroAviao}
                    className="anm26-plane"
                />
                <img
                    src="/nuvens.png"
                    alt=""
                    style={S.heroNuvens}
                    aria-hidden="true"
                />
                <h1 style={S.heroTitle}>Airport National Meeting 2026</h1>
                <p style={S.heroSub}>O maior encontro do setor aeroportuário nacional</p>
                <div style={S.metaBar}>
                    <span style={S.metaChip}>✈ 24 NOV 2026</span>
                    <span style={S.metaChip}>📍 Brasília — CICB</span>
                </div>
            </div>

            {/* Layout: side deco (desktop) + form card */}
            <div className="anm26-layout">

                {/* Side decoration – visible only ≥1024px */}
                <aside className="anm26-side-deco" aria-hidden="true">
                    <img
                        src="/torres.png"
                        alt=""
                        className="anm26-towers"
                        style={{
                            width: "200px",
                            filter: "drop-shadow(0 0 24px rgba(9,213,226,0.4))",
                        }}
                    />
                    <img
                        src="/aviao-decolando.png"
                        alt=""
                        className="anm26-decolando"
                        style={{
                            width: "190px",
                            filter: "drop-shadow(0 0 16px rgba(9,213,226,0.3))",
                        }}
                    />
                    {/* Info card */}
                    <div style={{
                        background: "rgba(9,213,226,0.07)",
                        border: "1px solid rgba(9,213,226,0.18)",
                        borderRadius: "16px",
                        padding: "20px",
                        width: "100%",
                        boxSizing: "border-box",
                    }}>
                        <p style={{ margin: "0 0 12px", fontSize: "11px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.12em", color: CYAN }}>
                            Sobre o Evento
                        </p>
                        <p style={{ margin: "0 0 12px", fontSize: "13px", color: "rgba(200,220,255,0.85)", lineHeight: 1.65 }}>
                            O <strong style={{ color: "#fff" }}>ANM</strong> reúne líderes, especialistas e parceiros do setor aeroportuário em um encontro de alto nível.
                        </p>
                        {[
                            { icon: "📅", label: "Data", val: "24 de Novembro de 2026" },
                            { icon: "📍", label: "Local", val: "CICB — Brasília, DF" },
                        ].map((item) => (
                            <div key={item.label} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginTop: "10px" }}>
                                <span style={{ fontSize: "14px", lineHeight: 1.4 }}>{item.icon}</span>
                                <div>
                                    <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.1em", color: CYAN, fontWeight: 700 }}>{item.label}</div>
                                    <div style={{ fontSize: "13px", color: "#e0ecff" }}>{item.val}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </aside>

                {/* Form card */}
                <div style={S.card}>
                    <div style={S.cardGlowTL} />
                    <div style={S.cardGlowBR} />

                    {eventNotFound ? (
                        <div style={S.notFoundBox}>Evento não encontrado.</div>
                    ) : submitted ? (
                        <div style={S.successBox}>
                            <div style={S.successIconWrap}>✅</div>
                            <h2 style={S.successTitle}>Inscrição Realizada!</h2>
                            <p style={S.successText}>
                                Sua inscrição foi enviada com sucesso e está pendente de aprovação.<br />
                                Em breve você receberá um e-mail de confirmação.<br />
                                <strong style={{ color: CYAN }}>Obrigado!</strong>
                            </p>
                        </div>
                    ) : (
                        <form id="register-form" onSubmit={handleSubmit} noValidate>
                            <SectionTitle>Dados Pessoais</SectionTitle>
                            <div style={S.grid}>
                                <Field
                                    id="field-name"
                                    label="Nome Completo"
                                    name="name"
                                    placeholder="Seu nome completo"
                                    required
                                    value={formData.name}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={errors.name}
                                    disabled={loading}
                                />
                                <Field
                                    id="field-name-tag"
                                    label="Nome do Crachá"
                                    name="name_tag"
                                    placeholder="Nome para o crachá"
                                    required
                                    value={formData.name_tag}
                                    onChange={onChange}
                                    error={errors.name_tag}
                                    disabled={loading}
                                />
                                <SelectField
                                    id="field-event-category"
                                    label="Categoria"
                                    name="event_category"
                                    options={["Participante", "Patrocinador", "Organização", "Painelista", "Staff"]}
                                    required
                                    value={formData.event_category}
                                    onChange={onChange}
                                    error={errors.event_category}
                                    disabled={loading}
                                    placeholder="Selecione a categoria"
                                />

                                {/* Checkbox Estrangeiro */}
                                <CheckboxField
                                    id="field-is-foreigner"
                                    label="Não tenho CPF / Estrangeiro"
                                    name="isForeigner"
                                    checked={isForeigner}
                                    onChange={onForeignerChange}
                                    disabled={loading}
                                />

                                {/* CPF ou Passaporte - condicional */}
                                {!isForeigner ? (
                                    <Field
                                        id="field-cpf"
                                        label="CPF"
                                        name="cpf"
                                        mask="999.999.999-99"
                                        placeholder="000.000.000-00"
                                        required
                                        value={formData.cpf}
                                        onChange={onChange}
                                        error={errors.cpf}
                                        disabled={loading}
                                    />
                                ) : (
                                    <Field
                                        id="field-passport"
                                        label="Passaporte"
                                        name="passport"
                                        placeholder="Número do passaporte"
                                        required
                                        value={formData.passport}
                                        onChange={onChange}
                                        error={errors.passport}
                                        disabled={loading}
                                    />
                                )}

                                <Field
                                    id="field-email"
                                    label="E-mail"
                                    name="email"
                                    type="email"
                                    placeholder="seu@email.com"
                                    required
                                    value={formData.email}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={errors.email}
                                    disabled={loading}
                                />
                                <Field
                                    id="field-email-confirmation"
                                    label="Confirme o E-mail"
                                    name="emailConfirmation"
                                    type="email"
                                    placeholder="Digite o e-mail novamente"
                                    required
                                    value={formData.emailConfirmation}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={errors.emailConfirmation}
                                    disabled={loading}
                                />
                                <Field
                                    id="field-phone"
                                    label="Telefone"
                                    name="phone"
                                    mask="+55 (99) 9 9999-9999"
                                    placeholder="+55 (00) 9 0000-0000"
                                    required
                                    value={formData.phone}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={errors.phone}
                                    disabled={loading}
                                />

                                <SelectField
                                    id="field-gender"
                                    label="Gênero"
                                    name="gender"
                                    options={["Masculino", "Feminino", "Prefiro não informar"]}
                                    required
                                    value={formData.gender}
                                    onChange={onChange}
                                    error={errors.gender}
                                    disabled={loading}
                                    placeholder="Selecione o gênero"
                                />

                                {/* Estado - apenas para brasileiros */}
                                {!isForeigner && (
                                    <SelectField
                                        id="field-address"
                                        label="Estado"
                                        name="address"
                                        options={BRAZILIAN_STATES}
                                        required
                                        value={formData.address}
                                        onChange={onChange}
                                        error={errors.address}
                                        disabled={loading}
                                        placeholder="Selecione o estado"
                                    />
                                )}
                            </div>

                            <Divider />

                            <SectionTitle>Dados Profissionais</SectionTitle>
                            <div style={S.grid}>
                                <Field
                                    id="field-company"
                                    label="Empresa / Instituição"
                                    name="company"
                                    placeholder="Nome da empresa ou órgão"
                                    required
                                    value={formData.company}
                                    onChange={onChange}
                                    error={errors.company}
                                    disabled={loading}
                                />
                                <Field
                                    id="field-role"
                                    label="Cargo"
                                    name="role"
                                    placeholder="Seu cargo"
                                    required
                                    value={formData.role}
                                    onChange={onChange}
                                    error={errors.role}
                                    disabled={loading}
                                />

                                <SelectField
                                    id="field-extras"
                                    label="Setor"
                                    name="extras"
                                    options={["Público", "Privado"]}
                                    required
                                    value={formData.extras}
                                    onChange={onChange}
                                    error={errors.extras}
                                    disabled={loading}
                                    placeholder="Selecione o setor"
                                />

                                <SelectField
                                    id="field-custom-fields"
                                    label="Área"
                                    name="custom_fields"
                                    options={formData.extras ? (AREA_OPTIONS[formData.extras] || []) : []}
                                    required
                                    value={formData.custom_fields}
                                    onChange={onChange}
                                    error={errors.custom_fields}
                                    disabled={loading || !formData.extras}
                                    placeholder={formData.extras ? "Selecione a área" : "Selecione primeiro o Setor"}
                                />
                            </div>

                            {/* Sponsor selector - só mostra se showSponsorField for true */}
                            {showSponsorField && (
                                <>
                                    <Divider />
                                    <SectionTitle>Patrocinador</SectionTitle>
                                    <div style={{ ...S.fieldGroup, marginTop: "0" }}>
                                        <label htmlFor="field-sponsor" style={S.label}>
                                            Patrocinador *
                                        </label>
                                        <select
                                            id="field-sponsor"
                                            name="sponsor_id"
                                            value={formData.sponsor_id}
                                            onChange={onChange}
                                            style={{
                                                ...S.select,
                                                ...(errors.sponsor_id ? S.inputError : {}),
                                            }}
                                            disabled={loading || sponsors.length === 0}
                                        >
                                            <option value="">
                                                {sponsors.length === 0
                                                    ? "Carregando patrocinadores..."
                                                    : "Selecione um patrocinador"}
                                            </option>
                                            {sponsors.map((sp) => {
                                                const isFull =
                                                    sp.total_slots > 0 &&
                                                    sp.occupied_slots >= sp.total_slots;
                                                return (
                                                    <option
                                                        key={sp.id}
                                                        value={sp.id}
                                                        disabled={isFull}
                                                    >
                                                        {sp.name}
                                                        {isFull ? " — VAGAS ESGOTADAS" : ""}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        {errors.sponsor_id && (
                                            <span style={S.errorMsg}>⚠ {errors.sponsor_id}</span>
                                        )}
                                        {sponsorFull && (
                                            <span style={S.sponsorNote}>
                                                ⚠ Limite de vagas atingido para este patrocinador.
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Patrocinador travado (página exclusiva do patrocinador) */}
                            {lockedSponsor && (
                                <div style={{ ...S.fieldGroup, marginTop: "8px" }}>
                                    <label style={S.label}>Patrocinador</label>
                                    <div
                                        style={{
                                            ...S.input,
                                            background: "rgba(9,213,226,0.05)",
                                            color: CYAN,
                                            cursor: "not-allowed",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            border: "1px solid rgba(9,213,226,0.2)",
                                        }}
                                    >
                                        ✈ {lockedSponsor.name}
                                    </div>
                                </div>
                            )}

                            <div style={S.termsContainer}>
                                <div style={S.termsCheckbox}>
                                    <input
                                        id="accept-terms"
                                        type="checkbox"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        style={{
                                            width: "18px",
                                            height: "18px",
                                            marginTop: "2px",
                                            cursor: "pointer",
                                            flexShrink: 0,
                                        }}
                                    />

                                    <label
                                        htmlFor="accept-terms"
                                        style={{
                                            color: "rgba(200,220,255,0.85)",
                                            lineHeight: 1.6,
                                            fontSize: "13px",
                                        }}
                                    >
                                        Declaro que li, compreendi e concordo com os {" "}
                                        <span
                                            style={S.termsLink}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowTerms(!showTerms);
                                            }}
                                        >
                                            Termos de Uso, Tratamento de Dados Pessoais, Uso de Imagem e Compliance do Airport National Meeting – ANM 2026
                                        </span>
                                        .
                                    </label>
                                </div>

                                {errors.acceptedTerms && (
                                    <div style={S.errorMsg}>
                                        ⚠ {errors.acceptedTerms}
                                    </div>
                                )}

                                {showTerms && (
                                    <div style={S.termsBox}>
                                        <h3 style={{ marginTop: 0 }}>
                                            TERMOS DE USO, TRATAMENTO DE DADOS PESSOAIS, USO DE IMAGEM E COMPLIANCE
                                        </h3>

                                        <p>
                                            Ao realizar minha inscrição no Airport National Meeting – ANM 2026 (ANM 2026), declaro que li, compreendi e concordo com as condições abaixo:
                                        </p>

                                        <p><strong>1. Inscrição e participação:</strong> As informações fornecidas são de minha responsabilidade e serão utilizadas para viabilizar minha inscrição, credenciamento, comunicação, participação no evento e demais atividades necessárias à organização, realização e avaliação do ANM 2026.
                                        </p>

                                        <p>
                                            <strong>2. Tratamento de dados pessoais:</strong> Os dados pessoais informados na inscrição serão tratados pela ABR – Aeroportos do Brasil em conformidade com a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais – LGPD). Para viabilizar a organização, operacionalização e realização do ANM 2026, os dados poderão ser compartilhados com empresas contratadas que atuarão em nome da ABR, exclusivamente para as finalidades relacionadas ao evento, sendo mantidos pelo período necessário ao cumprimento dessas finalidades e das obrigações legais aplicáveis.
                                        </p>

                                        <p>
                                            <strong>3. Uso de imagem, voz e nome:</strong> O ANM 2026 poderá ser fotografado, filmado e transmitido ao vivo. Ao participar do evento, declaro estar ciente de que minha imagem, voz e nome poderão ser captados e utilizados pela ABR para registro histórico, divulgação institucional e comunicação das atividades relacionadas ao ANM 2026, em seus canais oficiais de comunicação, incluindo site, redes sociais, vídeos, publicações e demais materiais institucionais.
                                        </p>

                                        <p>
                                            <strong>4. Compartilhamento de dados com patrocinadores:</strong> Durante o ANM 2026, poderei, de forma livre e voluntária, autorizar o compartilhamento dos meus dados de contato (nome, empresa, cargo, e-mail) com patrocinadores oficiais do evento, mediante interação realizada em seus estandes ou por outro mecanismo disponibilizado pela organização. Essa interação será considerada minha manifestação expressa de vontade para o compartilhamento dos dados com o respectivo patrocinador, que passará a ser responsável pelo tratamento dessas informações, nos termos da legislação aplicável.
                                        </p>

                                        <p>
                                            <strong>5. Ética, Integridade e Compliance:</strong> A participação no ANM 2026 pressupõe a observância das normas aplicáveis ao evento e do Programa de Integridade e Compliance da ABR. Os participantes comprometem-se a atuar em conformidade com as políticas institucionais da ABR, disponíveis em: https://abr.aero/pt/governanca/
                                        </p>

                                        <p>
                                            <strong>6. Declaração:</strong> Declaro que li, compreendi e concordo com os Termos de Uso, Tratamento de Dados Pessoais, Uso de Imagem e Compliance do Airport National Meeting – ANM 2026.
                                        </p>
                                        <span
                                            style={S.termsClose}
                                            onClick={() => setShowTerms(false)}
                                        >
                                            ▲ Ocultar termos
                                        </span>
                                    </div>
                                )}
                            </div>

                            <button
                                id="btn-submit"
                                type="submit"
                                disabled={loading || sponsorFull}
                                style={{
                                    ...S.submitBtn,
                                    ...(loading || sponsorFull ? S.submitBtnDisabled : {}),
                                }}
                            >
                                {loading
                                    ? "Enviando..."
                                    : sponsorFull
                                        ? "Vagas Esgotadas"
                                        : "✈ Confirmar Inscrição"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubscribeToEvent;
